// lib/server-utils/encryption.ts
import crypto from 'crypto';

const SECURITY_CONFIG = {
  ENCRYPTION_ALGORITHM: 'aes-256-gcm' as const,
  ENCRYPTION_KEY_LENGTH: 32,
  ENCRYPTION_IV_LENGTH: 12,   // 12 bytes for GCM
  ENCRYPTION_TAG_LENGTH: 16,  // 16 bytes auth tag
} as const;

export class EncryptionService {
  private isInitialized = false;
  private key!: Buffer;
  private algorithm = SECURITY_CONFIG.ENCRYPTION_ALGORITHM;

  constructor() {
    const keyHex = process.env.COOKIE_ENCRYPTION_KEY || 
      '24dad785f710b45f7a90a6208a66eda157126ec10b6de1c8c623cd5bd602b803'; // Development fallback

    if (process.env.NODE_ENV === 'production' && !process.env.COOKIE_ENCRYPTION_KEY) {
      // In production (including build), we warn if missing but don't crash yet
      // This allows static export to proceed
      console.warn('EncryptionService: COOKIE_ENCRYPTION_KEY missing. Encryption will fail if called.');
      this.key = Buffer.from(keyHex, 'hex');
      return;
    }

    if (keyHex.length !== 64) {
      throw new Error('Encryption key must be 64 hex characters (32 bytes)');
    }

    this.key = Buffer.from(keyHex, 'hex');
    this.isInitialized = true;

    if (!this.validateKey()) {
      // If we used the fallback in production build, this might still pass or fail depending on the key
      // But we want to avoid crashing the whole module load
      if (process.env.NODE_ENV !== 'production') {
        throw new Error('EncryptionService initialization failed: Key validation failed');
      }
    }
  }

  private ensureInitialized() {
    if (process.env.NODE_ENV === 'production' && !process.env.COOKIE_ENCRYPTION_KEY) {
       throw new Error('COOKIE_ENCRYPTION_KEY environment variable is required in production');
    }
  }

  /** ======================
   *  Encrypt text using AES-256-GCM
   *  ====================== */
  encrypt(text: string): string {
    this.ensureInitialized();
    try {
      const iv = crypto.randomBytes(SECURITY_CONFIG.ENCRYPTION_IV_LENGTH);
      const cipher = crypto.createCipheriv(this.algorithm, this.key, iv);

      const encrypted = Buffer.concat([cipher.update(text, 'utf8'), cipher.final()]);
      const tag = cipher.getAuthTag();

      return `${iv.toString('hex')}:${tag.toString('hex')}:${encrypted.toString('hex')}`;
    } catch (error) {
      console.error('Encryption error:', error instanceof Error ? error.message : error);
      throw new Error('Failed to encrypt data');
    }
  }

  /** ======================
   *  Decrypt text and enforce AES-GCM format
   *  ====================== */
  decrypt(encryptedText: string): string {
    this.ensureInitialized();
    try {
      if (!encryptedText || typeof encryptedText !== 'string') {
        throw new Error('Encrypted text must be a non-empty string');
      }

      const parts = encryptedText.split(':');
      if (parts.length !== 3) {
        throw new Error(
          `Invalid encrypted text format. Expected "<iv_hex>:<auth_tag_hex>:<encrypted_hex>", got "${encryptedText}"`
        );
      }

      const [ivHex, tagHex, encryptedHex] = parts;

      if (
        ivHex.length !== SECURITY_CONFIG.ENCRYPTION_IV_LENGTH * 2 ||
        tagHex.length !== SECURITY_CONFIG.ENCRYPTION_TAG_LENGTH * 2 ||
        !/^[0-9a-fA-F]+$/.test(ivHex + tagHex + encryptedHex)
      ) {
        throw new Error('Encrypted text is not valid AES-256-GCM hex format');
      }

      const iv = Buffer.from(ivHex, 'hex');
      const tag = Buffer.from(tagHex, 'hex');
      const encrypted = Buffer.from(encryptedHex, 'hex');

      const decipher = crypto.createDecipheriv(this.algorithm, this.key, iv);
      decipher.setAuthTag(tag);

      const decrypted = Buffer.concat([decipher.update(encrypted), decipher.final()]);

      return decrypted.toString('utf8');
    } catch (error) {
      console.error('Decryption error:', error instanceof Error ? error.message : error);
      throw new Error('Failed to decrypt data: Invalid or corrupted input');
    }
  }

  /** ======================
   *  NEW: Safe decrypt that handles both encrypted and plaintext
   *  ====================== */
  safeDecrypt(value: string): string | null {
    try {
      if (!value || typeof value !== 'string') {
        return null;
      }

      // Check if value looks encrypted (contains ':')
      if (!value.includes(':')) {
        // Plaintext - return as-is
        return value;
      }

      // Looks encrypted, try to decrypt
      return this.decrypt(value);
    } catch (error) {
      console.error('Safe decryption failed:', error instanceof Error ? error.message : error);
      return null;
    }
  }

  /** ======================
   *  Check if value appears to be encrypted
   *  ====================== */
  isEncrypted(value: string): boolean {
    if (!value || typeof value !== 'string') return false;
    
    const parts = value.split(':');
    if (parts.length !== 3) return false;
    
    const [ivHex, tagHex, encryptedHex] = parts;
    const expectedIvHexLen = SECURITY_CONFIG.ENCRYPTION_IV_LENGTH * 2;
    const expectedTagHexLen = SECURITY_CONFIG.ENCRYPTION_TAG_LENGTH * 2;
    
    return (
      ivHex.length === expectedIvHexLen &&
      tagHex.length === expectedTagHexLen &&
      /^[0-9a-fA-F]+$/.test(ivHex + tagHex + encryptedHex)
    );
  }

  /** ======================
   *  Test encryption key
   *  ====================== */
  validateKey(): boolean {
    try {
      const testData = 'test';
      const encrypted = this.encrypt(testData);
      const decrypted = this.decrypt(encrypted);
      return decrypted === testData;
    } catch {
      return false;
    }
  }

  /** ======================
   *  Mask sensitive data for logs
   *  ====================== */
  maskSensitiveData(data: string): string {
    if (!data || data.length < 8) return '***';
    return `${data.substring(0, 4)}...${data.substring(data.length - 4)}`;
  }
}

// Singleton export
export const encryptionService = new EncryptionService();